<template>
    <div class="mx-auto">
        <div class="mb-5">
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4">
                <StatCard
                    v-for="stat in stats"
                    :title="stat.name"
                    :value="stat.stat"
                    :icon="stat.icon"
                    :icon-bg-class="stat.colorClass"
                    :loading="topicStatisticsLoading || configLoading" />
            </div>
        </div>

        <Card class="bg-sidebar-primary-foreground">
            <CardHeader>
                <!-- Mobile Layout for Chart Filters -->
                <div class="block sm:hidden space-y-3">
                    <TimeRangeFilter
                        :model-value="convertFilterKeyToTimeRangeValue(selectedTopicsTimeseriesDateFilter?.key || 'last24h')"
                        :disabled="configLoading || timeseriesLoading"
                        :include-live-option="false"
                        :include-all-time-option="false"
                        :include-date-options="false"
                        :include-short-ranges="false"
                        :hide-options="[]"
                        width="100%"
                        @update:model-value="onTimeRangeChange" />
                    <MultiSelect
                        v-model="selectedMetricKeys"
                        :options="listOfTopicsMetrics"
                        :placeholder="
                            metricsSelectionLoading ? 'Loading...' : listOfTopicsMetrics?.length ? 'Select metrics' : 'No metrics available'
                        "
                        :disabled="metricsSelectionLoading"
                        :option-label="'name'"
                        :track-by="'key'"
                        :show-tags="false"
                        :enable-refresh="true"
                        class="w-full"
                        @refresh="() => fetchTopicsTimeseries()" />
                </div>

                <!-- Desktop Layout for Chart Filters -->
                <div class="hidden sm:flex items-center justify-between">
                    <TimeRangeFilter
                        :model-value="convertFilterKeyToTimeRangeValue(selectedTopicsTimeseriesDateFilter?.key || 'last24h')"
                        :disabled="configLoading || timeseriesLoading"
                        :include-live-option="false"
                        :include-all-time-option="false"
                        :include-date-options="false"
                        :include-short-ranges="false"
                        :hide-options="[]"
                        @update:model-value="onTimeRangeChange" />
                    <div class="flex items-center justify-end">
                        <MultiSelect
                            v-model="selectedMetricKeys"
                            :options="listOfTopicsMetrics"
                            :placeholder="
                                metricsSelectionLoading ? 'Loading...' : listOfTopicsMetrics?.length ? 'Select metrics' : 'No metrics available'
                            "
                            :disabled="metricsSelectionLoading"
                            :option-label="'name'"
                            :track-by="'key'"
                            :show-tags="false"
                            :enable-refresh="true"
                            @refresh="() => fetchTopicsTimeseries()" />
                    </div>
                </div>
            </CardHeader>
            <CardContent class="pt-0">
                <HighChart :chart-options="chartOptions" :loading="configLoading || timeseriesLoading || isInitializing || !topicsTimeseriesData" />
            </CardContent>
        </Card>

        <div class="table-page-container mt-10 mb-5">
            <div class="table-page-body">
                <!-- Search and Filters -->
                <div>
                    <div class="flex items-center gap-3 mb-3">
                        <h3 class="font-semibold">Topics</h3>
                    </div>
                    <div class="flex flex-col sm:flex-row justify-between mb-3 gap-3 sm:items-end">
                        <SearchInput
                            :loading="configLoading || topicTableDataLoading"
                            placeholder="Search by name..."
                            class="w-full sm:w-auto"
                            :enable-refresh="true"
                            @search="(input) => (searchQuery = input)"
                            @refresh="refreshTopicsTable" />
                    </div>

                    <div class="mt-2">
                        <ScrollArea
                            class="w-full border shadow-md rounded-md"
                            :class="
                                ((configLoading || topicTableDataLoading) && (pageSize || 10) <= 10) ||
                                (!(configLoading || topicTableDataLoading) && (topicsStore.all?.length || 0) <= 10)
                                    ? 'h-auto'
                                    : 'h-70-dynamic'
                            ">
                            <Table>
                                <TableHeader class="sticky top-0 bg-background z-10">
                                    <TableRow class="bg-muted/50">
                                        <TableHead class="first:rounded-tl-md">Name</TableHead>
                                        <TableHead>Entity</TableHead>
                                        <TableHead>Volume</TableHead>
                                        <TableHead>Errors</TableHead>
                                        <TableHead>Written</TableHead>
                                        <TableHead>Last</TableHead>
                                        <TableHead class="last:rounded-tr-md"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    <!-- Skeleton rows when loading -->
                                    <template v-if="configLoading || topicTableDataLoading">
                                        <TableRow v-for="n in pageSize || 10" :key="`skeleton-${n}`">
                                            <TableCell><SkeletonLoader preset="table-name" :loading="true" /></TableCell>
                                            <TableCell><SkeletonLoader preset="table-type" :loading="true" /></TableCell>
                                            <TableCell><SkeletonLoader preset="table-metric" :loading="true" /></TableCell>
                                            <TableCell><SkeletonLoader preset="table-number" :loading="true" /></TableCell>
                                            <TableCell><SkeletonLoader preset="table-metric" :loading="true" /></TableCell>
                                            <TableCell><SkeletonLoader preset="table-status" :loading="true" /></TableCell>
                                            <TableCell><SkeletonLoader preset="table-action" :loading="true" /></TableCell>
                                        </TableRow>
                                    </template>
                                    <!-- Actual data rows -->
                                    <template v-else>
                                        <TableRow v-for="topic in topicsStore.all" :key="topic.id">
                                            <TableCell>
                                                <Button
                                                    variant="link"
                                                    class="h-auto p-0 max-w-full text-left justify-start font-medium"
                                                    :title="topic.name"
                                                    @click="goToTopic(topic)">
                                                    <span
                                                        class="truncate max-w-[200px] sm:max-w-[250px] md:max-w-[300px] lg:max-w-[350px] xl:max-w-[400px]">
                                                        {{ topic.name }}
                                                    </span>
                                                </Button>
                                            </TableCell>
                                            <TableCell>
                                                <div class="flex items-center gap-2">
                                                    <Avatar class="h-6 w-6 bg-background dark:bg-slate-200 border border-border rounded-sm shrink-0">
                                                        <AvatarImage
                                                            :src="
                                                                getConnectorIcon(topic?.entity?.connector) || `/icons/${topic?.entity?.connector}.svg`
                                                            "
                                                            :alt="topic?.entity?.connector"
                                                            class="object-contain p-0.5" />
                                                        <AvatarFallback
                                                            class="bg-background dark:bg-slate-200 dark:text-slate-800 text-muted-foreground text-xs rounded-sm">
                                                            {{ topic?.entity?.connector?.charAt(0).toUpperCase() }}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <div class="flex flex-col gap-1 max-w-md">
                                                        <Button
                                                            variant="link"
                                                            class="h-auto p-0 max-w-full text-left justify-start font-medium"
                                                            :title="topic?.entity?.name"
                                                            @click="
                                                                $router.push(
                                                                    selectedConnectorRouterPath(topic?.entity?.entity_id, topic?.entity?.entity_type)
                                                                )
                                                            ">
                                                            <span class="truncate max-w-[200px] sm:max-w-[250px] md:max-w-[300px]">
                                                                {{ topic?.entity?.name }}
                                                            </span>
                                                        </Button>
                                                        <Badge variant="outline" class="text-xs uppercase">
                                                            {{ cleanEntityType(topic?.entity?.entity_type) }}
                                                        </Badge>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell
                                                :variant="
                                                    topicTableMetricsLoading ||
                                                    getFormattedValue('byteTotal', topic.metrics?.byteTotal).display === '-'
                                                        ? 'default'
                                                        : 'metric-badge'
                                                ">
                                                <SkeletonLoader v-if="topicTableMetricsLoading" preset="inline-metric" :loading="true" />
                                                <template v-else>
                                                    {{ getFormattedValue('byteTotal', topic.metrics?.byteTotal).display }}
                                                </template>
                                            </TableCell>
                                            <TableCell
                                                :variant="
                                                    topicTableMetricsLoading ||
                                                    getFormattedValue('recordErrorTotal', topic.metrics?.recordErrorTotal).display === '-'
                                                        ? 'default'
                                                        : 'metric-badge'
                                                ">
                                                <SkeletonLoader v-if="topicTableMetricsLoading" preset="inline-number" :loading="true" />
                                                <template v-else>
                                                    {{ getFormattedValue('recordErrorTotal', topic.metrics?.recordErrorTotal).display }}
                                                </template>
                                            </TableCell>
                                            <TableCell
                                                :variant="
                                                    topicTableMetricsLoading ||
                                                    getFormattedValue('recordWrittenTotal', topic.metrics?.recordWrittenTotal).display === '-'
                                                        ? 'default'
                                                        : 'metric-badge'
                                                ">
                                                <SkeletonLoader v-if="topicTableMetricsLoading" preset="inline-metric" :loading="true" />
                                                <template v-else>
                                                    {{ getFormattedValue('recordWrittenTotal', topic.metrics?.recordWrittenTotal).display }}
                                                </template>
                                            </TableCell>
                                            <TableCell
                                                :variant="
                                                    topicTableMetricsLoading ||
                                                    getFormattedValue('StreamingLastEvent', topic.metrics?.StreamingLastEvent).display === '-'
                                                        ? 'default'
                                                        : 'metric-badge'
                                                ">
                                                <SkeletonLoader v-if="topicTableMetricsLoading" preset="table-status" :loading="true" />
                                                <template v-else>
                                                    {{ getFormattedValue('StreamingLastEvent', topic.metrics?.StreamingLastEvent).display }}
                                                </template>
                                            </TableCell>
                                            <TableCell>
                                                <ActionMenu
                                                    variant="menu"
                                                    :show-copy-id="true"
                                                    :show-delete="userHasAccess([$Permissions.WRITE_TOPICS], authStore.user)"
                                                    :loading="topicTableDataLoading"
                                                    @copyId="onCopyTopicId(topic.id)"
                                                    @delete="onDeleteTopic(topic.name, topic.id)" />
                                            </TableCell>
                                        </TableRow>
                                        <TableEmpty v-if="!(configLoading || topicTableDataLoading) && topicsStore.all !== null && !topicsStore.all?.length" :colspan="7">
                                            <TableEmptyState />
                                        </TableEmpty>
                                    </template>
                                </TableBody>
                            </Table>
                            <ScrollBar orientation="horizontal" />
                            <ScrollBar orientation="vertical" />
                        </ScrollArea>
                    </div>
                    <TablePagination
                        v-if="topicsStore.all?.length > 0 || page > 0"
                        :current-page="page"
                        :page-size="pageSize"
                        :total-items="topicsStore.total"
                        :current-page-items-count="topicsStore.all?.length || 0"
                        item-type="topics"
                        :loading="topicTableDataLoading || configLoading"
                        @page-change="onTopicsPageChange"
                        @page-size-change="onItemsPerPageChange" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import _ from 'lodash';

import { formatChartXAxisLabels } from '../../utils/utils';
import { useTopicsStore } from '@/stores/topics.store';
import { useMetricsStore } from '@/stores/metrics.store';
import SearchInput from '@/components/global/SearchInput.vue';
import { MultiSelect } from '@/components/ui/multi-select';
import TimeRangeFilter from '@/components/filters/TimeRangeFilter.vue';
import HighChart from '../../components/HighChart.vue';
import StatCard from '../../components/global/StatCard.vue';
import { SkeletonLoader } from '@/components/ui/skeleton-loader';
import { formatter, getMetricFormattingInfo } from '../../utils/utils';
import { FILTER_DEFAULTS, TIME_RANGE_LABELS } from '../../utils/constants';
import { getTimeRangeLabel, createDateFilterFromTimeRange, convertFilterKeyToTimeRangeValue, getDatesBasedOnFilter } from '../../utils/utils';
import { AlertCircle, ChevronsLeft, Scale, List, RotateCcw } from 'lucide-vue-next';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableEmpty } from '@/components/ui/table';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import TablePagination from '@/components/TablePagination.vue';
import { useRoute, useRouter } from 'vue-router';
import moment from 'moment-timezone';
import { useLoaderStore } from '@/stores/loader.store';
import { getConnectorIcon } from '@/utils/constants';
import { useAuthStore } from '@/stores/auth.store';
import { userHasAccess } from '../../utils/utils';
import { useDeleteConfirmation } from '@/composables/useDialog';
import { useUrlFilters } from '@/composables/useUrlFilters';
import ActionMenu from '@/components/ActionMenu.vue';
import { TableEmptyState } from '@/components/ui/table-empty-state';
import { toast } from '@/composables/useToast';

const topicsStore = useTopicsStore();
const metricsStore = useMetricsStore();
const route = useRoute();
const router = useRouter();

const { registerFilter } = useUrlFilters({
    defaults: {
        // Pagination
        page: FILTER_DEFAULTS.PAGINATION.DEFAULT_PAGE,
        pageSize: FILTER_DEFAULTS.PAGINATION.DEFAULT_PAGE_SIZE,

        // Search
        searchQuery: null,

        // Date filter for timeseries chart
        selectedTopicsTimeseriesDateFilter: { key: FILTER_DEFAULTS.DATE_FILTERS.LAST_24H },

        // Metrics selection
        allSelectedMetricsForTopicsChart: []
    },

    // Custom serializers for complex objects
    serializers: {
        selectedTopicsTimeseriesDateFilter: (value) => (value && Object.keys(value).length ? JSON.stringify(value) : null),
        searchQuery: (value) => value || null,
        allSelectedMetricsForTopicsChart: (value) => JSON.stringify(value)
    },

    // Custom deserializers
    deserializers: {
        selectedTopicsTimeseriesDateFilter: (value) => {
            try {
                return value ? JSON.parse(value) : { key: FILTER_DEFAULTS.DATE_FILTERS.LAST_24H };
            } catch {
                return { key: FILTER_DEFAULTS.DATE_FILTERS.LAST_24H };
            }
        },
        searchQuery: (value) => value || null,
        allSelectedMetricsForTopicsChart: (value) => {
            try {
                return value ? JSON.parse(value) : [];
            } catch {
                return [];
            }
        }
    }
});

// Register all filters and get reactive references
const page = registerFilter('page', 0);
const pageSize = registerFilter('pageSize', 10);
const searchQuery = registerFilter('searchQuery', null);
const selectedTopicsTimeseriesDateFilter = registerFilter('selectedTopicsTimeseriesDateFilter', { key: FILTER_DEFAULTS.DATE_FILTERS.LAST_24H });
const allSelectedMetricsForTopicsChart = registerFilter('allSelectedMetricsForTopicsChart');

const configLoading = ref(false);
const topicTableDataLoading = ref(false);
const topicStatisticsLoading = ref(false);
const timeseriesLoading = ref(false);
const isInitializing = ref(true);

const topicStatistics = ref(null);
const topicsTimeseriesData = ref([]);

const loaderStore = useLoaderStore();

const topicTableMetricsLoading = computed(() => loaderStore.isLoading('metrics.topics.fetch.table'));

// Combined loading state for metrics selection
const metricsSelectionLoading = computed(() => {
    return timeseriesLoading.value || isInitializing.value;
});

const selectedMetricKeys = computed({
    get() {
        return allSelectedMetricsForTopicsChart.value.map((metric) => metric.key);
    },
    set(keys) {
        allSelectedMetricsForTopicsChart.value = keys.map((key) => listOfTopicsMetrics.value.find((metric) => metric.key === key)).filter(Boolean);
    }
});

onMounted(async () => {
    configLoading.value = true;
    topicTableDataLoading.value = true;
    topicStatisticsLoading.value = true;

    try {
        await Promise.all([
            fetchTopicsTableData(),
            topicsStore.fetchTopicConfigs().then(() => {
                configLoading.value = false;
            }),
            fetchTopicsTimeseries(),
            topicsStore.fetchTopicStatistics().then(() => {
                topicStatistics.value = topicsStore.statistics;
                topicStatisticsLoading.value = false;
            })
        ]);
    } catch (error) {
        console.error('Error loading topics data:', error);
    } finally {
        configLoading.value = false;
        topicTableDataLoading.value = false;
        topicStatisticsLoading.value = false;
        isInitializing.value = false;

        // Set default metrics if none are selected and metrics are available
        if (!allSelectedMetricsForTopicsChart.value.length && listOfTopicsMetrics.value.length) {
            const defaultTo = ['byteTotal'];
            const defaultMetrics = listOfTopicsMetrics.value.filter((x) => defaultTo.includes(x.key));
            allSelectedMetricsForTopicsChart.value = defaultMetrics.length ? defaultMetrics : [listOfTopicsMetrics.value[0]];
        }
    }
});

const stats = computed(() => {
    const [volume, errors, eventsSent, written] = ['byteTotal', 'recordErrorTotal', 'recordSendTotal', 'recordWrittenTotal'].map((metricKey) => {
        return {
            metric: metricKey,
            displayValue: getFormattedValue(metricKey, topicStatistics.value?.[metricKey]).display
        };
    });

    const topicsTotal = topicStatistics.value?.topicTotal;

    return [
        {
            id: 3,
            name: 'Topics',
            stat: topicsTotal,
            icon: List,
            colorClass: 'bg-primary'
        },
        {
            id: 0,
            name: 'Volume',
            stat: volume.displayValue,
            icon: Scale,
            colorClass: 'bg-chart-4'
        },
        {
            id: 1,
            name: 'Errors',
            stat: errors.displayValue,
            icon: AlertCircle,
            colorClass: 'bg-destructive'
        },
        {
            id: 2,
            name: 'Events',
            stat: eventsSent.displayValue,
            icon: ChevronsLeft,
            colorClass: 'bg-chart-1'
        }
    ];
});

const chartOptions = computed(() => {
    // Don't return chart options if data isn't ready
    if (configLoading.value || timeseriesLoading.value || isInitializing.value || !topicsTimeseriesData.value) {
        return null;
    }

    const titleText = getTimeRangeLabel(selectedTopicsTimeseriesDateFilter.value?.key) || TIME_RANGE_LABELS.LAST_24H;

    const metricKeys =
        _.cloneDeep(allSelectedMetricsForTopicsChart.value)
            ?.map((x) => (x.key ? { key: x.key, name: x.name } : null))
            .filter((x) => x) || [];

    const dataForEachSelectedMetric = metricKeys
        .map(({ key, name }) => {
            const topicsMetricData = topicsTimeseriesData.value?.topics?.topics_page?.[key];

            if (topicsMetricData?.length) {
                return [
                    {
                        metricKey: key,
                        metricData: topicsMetricData,
                        metricInfo: {
                            ...getMetricFormattingInfo(key, topicsStore.configs?.metrics),
                            name
                        }
                    }
                ];
            }

            return {
                metricKey: key,
                metricData: topicsMetricData,
                metricInfo: {
                    ...getMetricFormattingInfo(key, topicsStore.configs?.metrics),
                    name
                }
            };
        })
        .flat();

    const seriesAndYAxes = dataForEachSelectedMetric.map((metric, index) => {
        const yAxis = {
            title: {
                text: metric.metricInfo?.name
            },
            labels: {
                formatter: function () {
                    return formatter(this.value, { unit: metric.metricInfo?.unit }).display;
                }
            },
            opposite: index % 2 !== 0
        };

        const series = {
            name: metric.metricInfo?.name,
            data: metric.metricData?.map((dataItem) => {
                const localTime = moment.utc(dataItem.timestamp).tz(moment.tz.guess()).toDate();
                return {
                    x: localTime,
                    y: dataItem.value
                };
            }),
            type: 'line',
            yAxis: index,
            custom: {
                unit: metric.metricInfo?.unit
            },
            dataLabels: {
                enabled: true,
                formatter: function () {
                    return formatter(this.y, { unit: metric.metricInfo?.unit }).display;
                }
            }
        };

        return { series, yAxis };
    });

    // Split the series and yAxis configurations
    const series = seriesAndYAxes.map((sy) => sy.series);
    const yAxis = seriesAndYAxes.map((sy) => sy.yAxis);

    const options = {
        time: {
            timezone: moment.tz.guess()
        },
        chart: {
            zoomType: 'xy'
        },
        title: {
            text: titleText
        },
        subtitle: {
            text: (() => {
                const filterKey = selectedTopicsTimeseriesDateFilter.value?.key || 'last24h';
                const dateRange = getDatesBasedOnFilter ? getDatesBasedOnFilter(filterKey) : { timeUnit: 'hour' };
                const timeUnit = dateRange.timeUnit || 'hour';
                return timeUnit === 'hour'
                    ? 'Hourly Data'
                    : timeUnit === 'minute'
                        ? 'Minute Data'
                        : timeUnit === 'month'
                            ? 'Monthly Data'
                            : 'Daily Data';
            })()
        },
        xAxis: {
            type: 'datetime',
            labels: {
                formatter: formatChartXAxisLabels
            }
        },
        yAxis: yAxis,
        series: series,
        tooltip: {
            shared: true,
            crosshairs: true,
            formatter: function () {
                const localTime = moment(new Date(this.x)).tz(moment.tz.guess()).format('YYYY-MM-DD HH:mm:ss');

                let tooltip = `${localTime}<br>`;
                this.points.forEach((point) => {
                    tooltip += `<b>${point.series.name}:</b> ${formatter(point.y, { unit: point.series.userOptions.custom?.unit }).display}<br>`;
                });
                return tooltip;
            }
        }
    };

    return options;
});

const listOfTopicsMetrics = computed(() => {
    let topicMetricsKeys = Object.keys(topicsTimeseriesData.value?.topics?.topics_page || {});

    const keysToReturn = topicMetricsKeys
        .map((key) => {
            const metricInfo = getMetricFormattingInfo(key, topicsStore.configs?.metrics);

            if (!metricInfo) {
                return null;
            }

            return {
                ...metricInfo,
                key,
                name: metricInfo.name
            };
        })
        .filter((x) => x && topicsTimeseriesData.value?.topics?.topics_page?.[x.key]?.length);

    return _.orderBy(keysToReturn, 'name', 'asc');
});

// Separate computed for just tracking metric keys (for watcher optimization)
const availableMetricKeys = computed(() => {
    return listOfTopicsMetrics.value.map((m) => m.key).sort();
});

watch(
    () => availableMetricKeys.value,
    (newKeys, oldKeys) => {
        // Don't run during initial component setup
        if (isInitializing.value) {
            return;
        }

        // Avoid infinite loops by checking if the keys actually changed
        if (JSON.stringify(newKeys) === JSON.stringify(oldKeys)) {
            return;
        }

        const defaultTo = ['byteTotal'];
        const availableMetrics = listOfTopicsMetrics.value; // Get full metrics objects

        // Validate and clear invalid metrics to prevent crashes
        if (!newKeys?.length) {
            // No metrics available - clear selection
            if (allSelectedMetricsForTopicsChart.value.length) {
                allSelectedMetricsForTopicsChart.value = [];
            }
            return;
        }

        // Only set default metrics if none are selected
        if (!allSelectedMetricsForTopicsChart.value.length && availableMetrics.length) {
            const defaultMetrics = availableMetrics.filter((x) => defaultTo.includes(x.key));
            allSelectedMetricsForTopicsChart.value = defaultMetrics.length ? defaultMetrics : [availableMetrics[0]];
        }

        // If we have selected metrics, validate they still exist in available metrics
        else if (allSelectedMetricsForTopicsChart.value.length && availableMetrics.length) {
            const validMetrics = allSelectedMetricsForTopicsChart.value.filter((selected) =>
                availableMetrics.find((available) => available.key === selected.key)
            );

            // Only update if some metrics became invalid
            if (validMetrics.length !== allSelectedMetricsForTopicsChart.value.length) {
                allSelectedMetricsForTopicsChart.value = validMetrics;
            }
        }
    },
    { flush: 'post' } // Run after DOM updates to avoid timing issues
);

// Automatic data refetching watchers - only for table data
watch(
    [page, pageSize],
    async () => {
        if (!isInitializing.value) {
            topicTableDataLoading.value = true;
            await fetchTopicsTableData();
        }
    },
    { deep: true }
);

// Debounced search watcher - only for table data
watch(
    () => searchQuery.value,
    _.debounce(async () => {
        if (!isInitializing.value) {
            page.value = 0; // Reset to first page on search
            topicTableDataLoading.value = true;
            await fetchTopicsTableData();
        }
    }, 700)
);

// Watch date filter changes - only affects timeseries/chart data
watch(
    () => selectedTopicsTimeseriesDateFilter.value,
    async (newFilter) => {
        if (!isInitializing.value && newFilter) {
            timeseriesLoading.value = true;
            await fetchTopicsTimeseries();
        }
    },
    { deep: true }
);

async function fetchTopicsTableData() {
    try {
        await topicsStore.fetch({
            page_size: pageSize.value,
            page_number: page.value,
            partial_name: searchQuery.value
        });
    } catch (error) {
        console.error('Error fetching topics table data:', error);
    } finally {
        topicTableDataLoading.value = false;
    }
}

const refreshTopicsTable = async () => {
    page.value = 0; // Reset to first page
    topicTableDataLoading.value = true;
    await fetchTopicsTableData();
};

const getFormattedValue = (metricKey, value) => {
    const metricInfo = getMetricFormattingInfo(metricKey, topicsStore.configs?.metrics);

    return formatter(value, metricInfo || {});
};

const fetchTopicsTimeseries = async () => {
    timeseriesLoading.value = true;

    const defaultDateFilter =
        selectedTopicsTimeseriesDateFilter.value?.key === 'last24h' ||
        !selectedTopicsTimeseriesDateFilter.value ||
        !selectedTopicsTimeseriesDateFilter.value?.key;

    try {
        const timeseriesData = await metricsStore.topics.fetch(
            defaultDateFilter
                ? undefined
                : {
                    start: selectedTopicsTimeseriesDateFilter.value?.value?.start,
                    end: selectedTopicsTimeseriesDateFilter.value?.value?.end
                }
        );

        topicsTimeseriesData.value = timeseriesData;

        return timeseriesData;
    } catch (err) {
    } finally {
        timeseriesLoading.value = false;
    }
};

function selectedConnectorRouterPath(id, type) {
    if (type?.includes('source')) {
        return { name: 'source-details', params: { id } };
    } else if (type?.includes('destination')) {
        return { name: 'destination-details', params: { id } };
    } else if (type?.includes('transform')) {
        return { name: 'transform-details', params: { id } };
    }
    return { name: 'destination-details', params: { id } };
}

const goToTopic = (topic) => {
    router.push({ name: 'topic-details', params: { topic_id: topic.id } });
};

const authStore = useAuthStore();

/**
 * Copy topic ID to clipboard
 */
async function onCopyTopicId(topicId) {
    try {
        await navigator.clipboard.writeText(topicId);
        // Show success toast
        toast.success('Copied!', {
            description: `Topic ID "${topicId}" copied to clipboard`
        });
    } catch (error) {
        console.error('Failed to copy topic ID:', error);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = topicId;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);

        toast.success('Copied!', {
            description: `Topic ID "${topicId}" copied to clipboard`
        });
    }
}

/**
 * Prompt and delete a topic by name, then refresh table.
 */
async function onDeleteTopic(name, id) {
    topicTableDataLoading.value = true;

    try {
        await useDeleteConfirmation({
            entity: 'topic',
            name,
            title: 'Delete Topic',
            message: `Are you sure you want to delete the topic <strong>"${name}"</strong>? This action cannot be undone.`,
            onConfirm: async () => {
                await topicsStore.delete(id);
            }
        });
    } catch (error) {
        // User cancelled or error occurred
        if (error.message) {
            console.error('Delete failed:', error);
        }
    } finally {
        topicTableDataLoading.value = false;
    }
}

function cleanEntityType(type) {
    return type?.replace(/s$/, '')?.toUpperCase() || '';
}

async function onTopicsPageChange(newPage) {
    try {
        // Temporarily disable watchers during batch update
        isInitializing.value = true;
        page.value = newPage;
        isInitializing.value = false;

        // Manually trigger the fetch
        topicTableDataLoading.value = true;
        await fetchTopicsTableData();
    } catch (error) {
        console.error('Error in topics operation:', error);
    }
}

async function onTimeRangeChange(timeRangeValue) {
    const dateFilter = createDateFilterFromTimeRange(timeRangeValue);
    selectedTopicsTimeseriesDateFilter.value = dateFilter;

    await fetchTopicsTimeseries();
}

async function onItemsPerPageChange(newPageSize) {
    try {
        // Temporarily disable watchers during batch update
        isInitializing.value = true;
        page.value = 0;
        pageSize.value = newPageSize;
        isInitializing.value = false;

        // Manually trigger the fetch
        topicTableDataLoading.value = true;
        await fetchTopicsTableData();
    } catch (error) {
        console.error('Error in topics operation:', error);
    }
}
</script>
